import { createSlice, PayloadAction, type UnknownAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { RootState } from '@/app/store';
import { DEFAULT_MEASUREMENT_UNIT, type MeasurementUnit } from '@/types/measurement';
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@/constants/currency';
import { fetchSupportedCurrencies } from '@/features/reference/referenceSlice';

type CurrencyPreferencesState = {
  selected: CurrencyCode;
  defaultCurrency: CurrencyCode;
  hasUserSelection: boolean;
};

type PreferencesState = {
  measurementUnit: MeasurementUnit;
  currency?: CurrencyPreferencesState;
};

const createCurrencyState = (): CurrencyPreferencesState => ({
  selected: DEFAULT_CURRENCY_CODE,
  defaultCurrency: DEFAULT_CURRENCY_CODE,
  hasUserSelection: false,
});

const initialState: PreferencesState = {
  measurementUnit: DEFAULT_MEASUREMENT_UNIT,
  currency: createCurrencyState(),
};

type PersistedCurrencyState = Partial<CurrencyPreferencesState>;

const normalizeCurrencyState = (value?: PersistedCurrencyState): CurrencyPreferencesState => {
  const base = createCurrencyState();
  if (!value) {
    return base;
  }

  return {
    selected: (value.selected as CurrencyCode) ?? base.selected,
    defaultCurrency: (value.defaultCurrency as CurrencyCode) ?? base.defaultCurrency,
    hasUserSelection: Boolean(value.hasUserSelection),
  } satisfies CurrencyPreferencesState;
};

const ensureCurrencyState = (state: PreferencesState): CurrencyPreferencesState => {
  if (!state.currency) {
    state.currency = createCurrencyState();
  }
  return state.currency;
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    setMeasurementUnit: (state, action: PayloadAction<MeasurementUnit>) => {
      state.measurementUnit = action.payload;
    },
    setSelectedCurrency: (state, action: PayloadAction<CurrencyCode>) => {
      const currencyState = ensureCurrencyState(state);
      currencyState.selected = action.payload;
      currencyState.hasUserSelection = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSupportedCurrencies.fulfilled, (state, action) => {
      const currencyState = ensureCurrencyState(state);
      const resolvedDefault = action.payload.defaultCurrency;
      currencyState.defaultCurrency = resolvedDefault;
      if (!currencyState.hasUserSelection) {
        currencyState.selected = resolvedDefault;
      }
    });

    builder.addMatcher(
      (action: UnknownAction): action is { type: typeof REHYDRATE; payload?: { preferences?: Partial<PreferencesState> } } =>
        action.type === REHYDRATE,
      (state, action) => {
        const incoming = action.payload?.preferences;
        if (!incoming) {
          return;
        }

        if (incoming.measurementUnit) {
          state.measurementUnit = incoming.measurementUnit as MeasurementUnit;
        }

        state.currency = normalizeCurrencyState(incoming.currency as PersistedCurrencyState | undefined);
      },
    );
  },
});

export const { setMeasurementUnit, setSelectedCurrency } = preferencesSlice.actions;

export const selectMeasurementUnit = (state: RootState) => state.preferences.measurementUnit;
export const selectSelectedCurrency = (state: RootState) => (state.preferences.currency ?? createCurrencyState()).selected;

export default preferencesSlice.reducer;
